import { LubooDialog } from './luboo-dialog.js';
import { setupChannel, destroyChannel } from '../mixins/data-channel.mixin.js';

/**
 * <custom-roles-editor> — Roles & Permissions editor.
 *
 * Generalized for both account-tier and property-tier RBAC. Pass URLs
 * via attributes so the same component drives both:
 *
 *   <custom-roles-editor
 *     api-base="/api/accounts/{aid}/roles"
 *     catalog-url="/api/accounts/{aid}/permission-catalog">
 *   </custom-roles-editor>
 *
 *   <custom-roles-editor
 *     api-base="/api/accounts/{aid}/properties/{pid}/roles"
 *     catalog-url="/api/accounts/{aid}/properties/{pid}/permission-catalog">
 *   </custom-roles-editor>
 *
 * Back-compat: if only `account-id` is set, defaults to account-tier URLs.
 *
 * Permissions render as draggable cards. Click +/× still works.
 * System roles are read-only in the UI (server enforces too).
 *
 * Live updates use surgical row insert/patch/remove against the role
 * list sidebar via BroadcastChannel:
 *   - account-tier: `custom_role_*` on `luboo:account:{aid}`
 *   - property-tier: `property_role_*` on `luboo:property:{pid}`
 * The editor pane on the right is only rebuilt when the currently-edited
 * role is the one being updated — peer-tab list mutations don't disturb
 * an in-progress edit.
 */

const ROLE_KIND_LABELS = {
  // account-tier
  owner: 'Owner',
  admin: 'Admin',
  member: 'Member',
  viewer: 'Viewer',
  // property-tier
  manager: 'Manager',
  staff: 'Staff',
};

class CustomRolesEditor extends HTMLElement {
  static get observedAttributes() {
    return ['account-id', 'api-base', 'catalog-url'];
  }

  // Mixin methods attached as instance fields (esbuild ordering safe).
  setupChannel = setupChannel;
  destroyChannel = destroyChannel;

  // Both tiers share these instance fields; _messageMap is rebuilt in
  // _wireBroadcastChannel() once we know the api-base shape.
  _messageMap = {
    custom_role_created: this._onRoleCreated,
    custom_role_updated: this._onRoleUpdated,
    custom_role_deleted: this._onRoleDeleted,
    property_role_created: this._onRoleCreated,
    property_role_updated: this._onRoleUpdated,
    property_role_deleted: this._onRoleDeleted,
  };

  constructor() {
    super();
    this._initialized = false;
    this._roles = [];
    this._catalog = {};
    this._selectedRoleId = null;
    this._selectedDetail = null;
    this._draftName = '';
    this._draftDescription = '';
    this._draftPermissions = new Set();
    this._dirty = false;
    this._filterAvailable = '';
    this._filterGranted = '';
    this._dragSource = null; // 'available' | 'granted' | null while dragging
    this._onClick = this._onClick.bind(this);
    this._onInput = this._onInput.bind(this);
    this._onDragStart = this._onDragStart.bind(this);
    this._onDragOver = this._onDragOver.bind(this);
    this._onDrop = this._onDrop.bind(this);
    this._onDragEnd = this._onDragEnd.bind(this);
  }

  connectedCallback() {
    // Document-level delegation so listeners survive pane moves.
    const inside = (e) => this.contains(e.target);
    this._scopedClick = (e) => inside(e) && this._onClick(e);
    this._scopedInput = (e) => inside(e) && this._onInput(e);
    this._scopedDragStart = (e) => inside(e) && this._onDragStart(e);
    this._scopedDragOver = (e) => inside(e) && this._onDragOver(e);
    this._scopedDrop = (e) => inside(e) && this._onDrop(e);
    this._scopedDragEnd = (e) => inside(e) && this._onDragEnd(e);
    document.addEventListener('click', this._scopedClick);
    document.addEventListener('input', this._scopedInput);
    document.addEventListener('dragstart', this._scopedDragStart);
    document.addEventListener('dragover', this._scopedDragOver);
    document.addEventListener('drop', this._scopedDrop);
    document.addEventListener('dragend', this._scopedDragEnd);
    if (!this._initialized) {
      this._initialized = true;
      this._renderLoading();
    }
    if (this._apiBase()) {
      this._wireBroadcastChannel();
      this.refresh();
    }
  }

  disconnectedCallback() {
    document.removeEventListener('click', this._scopedClick);
    document.removeEventListener('input', this._scopedInput);
    document.removeEventListener('dragstart', this._scopedDragStart);
    document.removeEventListener('dragover', this._scopedDragOver);
    document.removeEventListener('drop', this._scopedDrop);
    document.removeEventListener('dragend', this._scopedDragEnd);
    this.destroyChannel();
  }

  /**
   * Pick the right BroadcastChannel scope based on the api-base attribute
   * shape: `/api/accounts/{aid}/properties/{pid}/roles` → property-tier;
   * `/api/accounts/{aid}/roles` → account-tier. Falls back to no channel
   * (silent) if the api-base can't be parsed — same component is used in
   * unit tests with stub URLs.
   */
  _wireBroadcastChannel() {
    const apiBase = this._apiBase();
    if (!apiBase) return;
    const propertyMatch = apiBase.match(/\/properties\/([^/]+)\/roles$/);
    if (propertyMatch) {
      this.setupChannel(`luboo:property:${propertyMatch[1]}`);
      return;
    }
    const accountMatch = apiBase.match(/\/accounts\/([^/]+)\/roles$/);
    if (accountMatch) {
      this.setupChannel(`luboo:account:${accountMatch[1]}`);
    }
  }

  // ── Real-time event handlers (BCM-routed) ────────────────────────────

  /**
   * A new role row landed on the channel. Append it to the sidebar list
   * (and our in-memory _roles array) without rebuilding the editor pane.
   */
  _onRoleCreated(role) {
    if (!role?.id) return;
    if (this._roles.some((r) => r.id === role.id)) return;
    this._roles.push(role);
    this._appendRoleListItem(role);
  }

  /**
   * Patch a role row in place. If the patched role is the one currently
   * being edited AND the edit isn't dirty, refresh the editor pane too —
   * otherwise we keep the user's draft state intact and just update the
   * sidebar label / perm-count.
   */
  _onRoleUpdated(role) {
    if (!role?.id) return;
    const index = this._roles.findIndex((r) => r.id === role.id);
    if (index === -1) {
      this._roles.push(role);
      this._appendRoleListItem(role);
      return;
    }
    this._roles[index] = role;
    this._patchRoleListItem(role);
    if (this._selectedRoleId === role.id && !this._dirty) {
      // Refresh detail in the background so granted-perm counts stay
      // accurate. Don't await — we don't block the event loop.
      this._loadRoleDetail().then(() => this._renderEditor());
    }
  }

  /**
   * Role deleted on another tab. Drop the row; if the user was editing it,
   * clear the editor pane so they're not staring at a phantom role.
   */
  _onRoleDeleted(payload) {
    const id = payload?.id;
    if (!id) return;
    const index = this._roles.findIndex((r) => r.id === id);
    if (index === -1) return;
    this._roles.splice(index, 1);
    this._removeRoleListItem(id);
    if (this._selectedRoleId === id) {
      this._selectedRoleId = this._roles[0]?.id || null;
      this._selectedDetail = null;
      this._draftPermissions = new Set();
      this._dirty = false;
      if (this._selectedRoleId) {
        this._loadRoleDetail().then(() => this._renderEditor());
      } else {
        this._renderEditor();
      }
    }
  }

  // ── Surgical role-list helpers ───────────────────────────────────────

  _appendRoleListItem(role) {
    const list = this.querySelector('.custom-roles-list ul');
    if (!list) return; // shell not mounted yet — initial render will pick it up
    list.insertAdjacentHTML('beforeend', this._roleListItemHtml(role));
  }

  _patchRoleListItem(role) {
    const btn = this.querySelector(
      `[data-custom-roles-action="select-role"][data-role-id="${CSS.escape(role.id)}"]`
    );
    if (!btn) return;
    const li = btn.closest('li');
    if (!li) return;
    const fresh = document.createElement('template');
    fresh.innerHTML = this._roleListItemHtml(role).trim();
    const next = fresh.content.firstElementChild;
    if (next) li.replaceWith(next);
  }

  _removeRoleListItem(roleId) {
    const btn = this.querySelector(
      `[data-custom-roles-action="select-role"][data-role-id="${CSS.escape(roleId)}"]`
    );
    if (!btn) return;
    const li = btn.closest('li');
    if (li) li.remove();
  }

  /**
   * Single role row HTML — extracted from _renderRoleList so both full
   * render and surgical insert/patch share the same markup.
   */
  _roleListItemHtml(role) {
    const isSelected = role.id === this._selectedRoleId;
    const kindLabel = role.is_system
      ? `<span class="role-badge role-badge-system">${this._escape(ROLE_KIND_LABELS[role.system_kind] || 'System')}</span>`
      : '<span class="role-badge role-badge-custom">Custom</span>';
    return `
      <li>
        <button class="custom-roles-list-item${isSelected ? ' is-selected' : ''}"
                data-custom-roles-action="select-role"
                data-role-id="${this._escape(role.id)}">
          <span class="role-name">${this._escape(role.name)}</span>
          ${kindLabel}
          <span class="role-perm-count">${role.permission_count} perms</span>
        </button>
      </li>
    `;
  }

  _apiBase() {
    const explicit = this.getAttribute('api-base');
    if (explicit) return explicit;
    const accountId = this.getAttribute('account-id');
    if (accountId) return `/api/accounts/${accountId}/roles`;
    return null;
  }

  _catalogUrl() {
    const explicit = this.getAttribute('catalog-url');
    if (explicit) return explicit;
    const accountId = this.getAttribute('account-id');
    if (accountId) return `/api/accounts/${accountId}/permission-catalog`;
    return null;
  }

  async refresh() {
    const apiBase = this._apiBase();
    const catalogUrl = this._catalogUrl();
    if (!apiBase || !catalogUrl) {
      this._renderError('Missing api-base / catalog-url attributes');
      return;
    }
    try {
      const [rolesRes, catalogRes] = await Promise.all([
        fetch(apiBase, {
          headers: { Accept: 'application/json' },
          credentials: 'include',
        }),
        fetch(catalogUrl, {
          headers: { Accept: 'application/json' },
          credentials: 'include',
        }),
      ]);
      if (!rolesRes.ok || !catalogRes.ok) {
        this._renderError('Failed to load roles');
        return;
      }
      this._roles = await rolesRes.json();
      const catalog = await catalogRes.json();
      // Catalog may be {group: [str, ...]} (old shape) or
      // {group: [{name, description}, ...]} (new shape). Normalize.
      this._catalog = {};
      for (const [group, items] of Object.entries(catalog)) {
        this._catalog[group] = items.map((it) =>
          typeof it === 'string' ? { name: it, description: '' } : it
        );
      }
      if (!this._selectedRoleId && this._roles.length > 0) {
        this._selectedRoleId = this._roles[0].id;
      }
      await this._loadRoleDetail();
      this._render();
    } catch (err) {
      this._renderError(String(err));
    }
  }

  async _loadRoleDetail() {
    if (!this._selectedRoleId) {
      this._selectedDetail = null;
      this._draftPermissions = new Set();
      return;
    }
    try {
      const res = await fetch(
        `${this._apiBase()}/${this._selectedRoleId}`,
        { headers: { Accept: 'application/json' }, credentials: 'include' }
      );
      if (!res.ok) {
        this._selectedDetail = null;
        return;
      }
      this._selectedDetail = await res.json();
      this._draftName = this._selectedDetail.name;
      this._draftDescription = this._selectedDetail.description || '';
      this._draftPermissions = new Set(this._selectedDetail.permissions || []);
      this._dirty = false;
    } catch (err) {
      this._selectedDetail = null;
    }
  }

  _onClick(e) {
    const target = e.target;
    const roleEl = target.closest('[data-custom-roles-action="select-role"]');
    if (roleEl) {
      e.preventDefault();
      this._handleSelectRole(roleEl.getAttribute('data-role-id'));
      return;
    }
    if (target.closest('[data-custom-roles-action="new-role"]')) {
      e.preventDefault();
      this._openNewRoleDialog();
      return;
    }
    const cloneBtn = target.closest('[data-custom-roles-action="clone-role"]');
    if (cloneBtn) {
      e.preventDefault();
      this._openCloneRoleDialog(cloneBtn.getAttribute('data-role-id'));
      return;
    }
    if (target.closest('[data-custom-roles-action="delete-role"]')) {
      e.preventDefault();
      this._handleDeleteRole();
      return;
    }
    const grantBtn = target.closest(
      '[data-custom-roles-action="grant-permission"]'
    );
    if (grantBtn) {
      e.preventDefault();
      if (this._isSystemRole()) return;
      this._handleGrant(grantBtn.getAttribute('data-permission'));
      return;
    }
    const revokeBtn = target.closest(
      '[data-custom-roles-action="revoke-permission"]'
    );
    if (revokeBtn) {
      e.preventDefault();
      if (this._isSystemRole()) return;
      this._handleRevoke(revokeBtn.getAttribute('data-permission'));
      return;
    }
    if (target.closest('[data-custom-roles-action="save"]')) {
      e.preventDefault();
      this._handleSave();
      return;
    }
    if (target.closest('[data-custom-roles-action="cancel"]')) {
      e.preventDefault();
      this._handleCancel();
    }
  }

  _onInput(e) {
    if (e.target.matches('[data-custom-roles-field="name"]')) {
      this._draftName = e.target.value;
      this._dirty = true;
      this._refreshButtons();
    } else if (e.target.matches('[data-custom-roles-field="description"]')) {
      this._draftDescription = e.target.value;
      this._dirty = true;
      this._refreshButtons();
    } else if (e.target.matches('[data-perm-filter]')) {
      const which = e.target.getAttribute('data-perm-filter');
      if (which === 'available') this._filterAvailable = e.target.value;
      else if (which === 'granted') this._filterGranted = e.target.value;
      this._applyPermFilter(which);
    }
  }

  _applyPermFilter(which) {
    const root = this.querySelector(
      which === 'available' ? '.available-column' : '.granted-column'
    );
    if (!root) return;
    const q = (
      which === 'available' ? this._filterAvailable : this._filterGranted
    )
      .trim()
      .toLowerCase();
    const cards = root.querySelectorAll('[data-perm-card]');
    cards.forEach((card) => {
      if (!q) {
        card.style.display = '';
        return;
      }
      const code = card.querySelector('code');
      const desc = card.querySelector('.perm-description');
      const haystack = (
        (code ? code.textContent : '') +
        ' ' +
        (desc ? desc.textContent : '')
      ).toLowerCase();
      card.style.display = haystack.includes(q) ? '' : 'none';
    });
    // Available column has per-group headers — hide a group when all its
    // cards are hidden so the section header doesn't dangle alone.
    if (which === 'available') {
      root.querySelectorAll('.perm-group').forEach((group) => {
        if (group.classList.contains('is-empty')) return;
        const anyVisible = Array.from(
          group.querySelectorAll('[data-perm-card]')
        ).some((c) => c.style.display !== 'none');
        group.style.display = anyVisible ? '' : 'none';
      });
    }
  }

  // ── Drag-and-drop ─────────────────────────────────────────────

  _onDragStart(e) {
    const card = e.target.closest('[data-perm-card]');
    if (!card) return;
    if (this._isSystemRole()) {
      e.preventDefault();
      return;
    }
    const name = card.getAttribute('data-permission');
    const source = card.getAttribute('data-perm-source'); // 'available' | 'granted'
    this._dragSource = source;
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/x-permission', name);
    e.dataTransfer.setData('text/x-perm-source', source);
    card.classList.add('is-dragging');
    // Pre-highlight the destination column so the drop zone is obvious
    // immediately instead of only after the first dragover. The source's
    // own column is implicit (you're dragging from it) and never gets
    // the highlight — see _onDragOver below.
    const destSelector =
      source === 'available' ? '.granted-column' : '.available-column';
    const dest = this.querySelector(destSelector);
    if (dest) dest.classList.add('is-drop-target');
  }

  _onDragOver(e) {
    if (this._isSystemRole()) return;
    const target = e.target.closest('[data-perm-drop-target]');
    if (!target) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    // Don't highlight the column you're dragging FROM — only the opposite
    // side should be the drop target.
    const targetSide = target.getAttribute('data-perm-drop-target');
    if (this._dragSource && targetSide === this._dragSource) return;
    target.classList.add('is-drop-target');
  }

  _onDragEnd(e) {
    this._dragSource = null;
    this.querySelectorAll('.is-dragging').forEach((el) =>
      el.classList.remove('is-dragging')
    );
    this.querySelectorAll('.is-drop-target').forEach((el) =>
      el.classList.remove('is-drop-target')
    );
  }

  _onDrop(e) {
    if (this._isSystemRole()) return;
    const target = e.target.closest('[data-perm-drop-target]');
    if (!target) return;
    e.preventDefault();
    const name = e.dataTransfer.getData('text/x-permission');
    const source = e.dataTransfer.getData('text/x-perm-source');
    const destZone = target.getAttribute('data-perm-drop-target');
    if (!name) return;
    if (source === 'available' && destZone === 'granted') {
      this._handleGrant(name);
    } else if (source === 'granted' && destZone === 'available') {
      this._handleRevoke(name);
    }
    target.classList.remove('is-drop-target');
  }

  _isSystemRole() {
    return !!(this._selectedDetail && this._selectedDetail.is_system);
  }

  async _handleSelectRole(roleId) {
    if (this._dirty) {
      const confirmed = await LubooDialog.confirm(
        'You have unsaved changes to this role. Switch anyway and lose them?',
        {
          heading: 'Discard unsaved changes?',
          okLabel: 'Discard',
          cancelLabel: 'Keep editing',
        }
      );
      if (!confirmed) return;
    }
    this._selectedRoleId = roleId;
    this._filterAvailable = '';
    this._filterGranted = '';
    await this._loadRoleDetail();
    this._render();
  }

  _handleGrant(permission) {
    if (!this._selectedDetail) return;
    this._draftPermissions.add(permission);
    this._dirty = true;
    this._renderEditor();
  }

  _handleRevoke(permission) {
    if (!this._selectedDetail) return;
    this._draftPermissions.delete(permission);
    this._dirty = true;
    this._renderEditor();
  }

  _handleCancel() {
    if (!this._selectedDetail) return;
    this._draftName = this._selectedDetail.name;
    this._draftDescription = this._selectedDetail.description || '';
    this._draftPermissions = new Set(this._selectedDetail.permissions || []);
    this._dirty = false;
    this._renderEditor();
  }

  async _handleSave() {
    if (!this._selectedRoleId || !this._dirty) return;
    if (this._isSystemRole()) {
      // Only permission set can change for system roles (server still
      // rejects renaming a system role)
    }
    try {
      const permsRes = await fetch(
        `${this._apiBase()}/${this._selectedRoleId}/permissions`,
        {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
          },
          credentials: 'include',
          body: JSON.stringify({
            permissions: Array.from(this._draftPermissions),
          }),
        }
      );
      if (!permsRes.ok) {
        const err = await permsRes.json().catch(() => ({}));
        await LubooDialog.alert(
          err.detail || 'Could not save permissions.',
          { heading: 'Save failed' }
        );
        return;
      }
      if (
        this._selectedDetail &&
        !this._selectedDetail.is_system &&
        (this._draftName !== this._selectedDetail.name ||
          this._draftDescription !== (this._selectedDetail.description || ''))
      ) {
        const metaRes = await fetch(
          `${this._apiBase()}/${this._selectedRoleId}`,
          {
            method: 'PUT',
            headers: {
              'Content-Type': 'application/json',
              Accept: 'application/json',
            },
            credentials: 'include',
            body: JSON.stringify({
              name: this._draftName,
              description: this._draftDescription,
            }),
          }
        );
        if (!metaRes.ok) {
          const err = await metaRes.json().catch(() => ({}));
          await LubooDialog.alert(
            err.detail || 'Could not save role details.',
            { heading: 'Save failed' }
          );
          return;
        }
      }
      this._dirty = false;
      await this.refresh();
    } catch (err) {
      await LubooDialog.alert(String(err), { heading: 'Save failed' });
    }
  }

  async _handleDeleteRole() {
    if (!this._selectedDetail || this._selectedDetail.is_system) return;
    const confirmed = await LubooDialog.confirm(
      `Permanently delete the role "${this._selectedDetail.name}"? Users currently assigned to it must be reassigned first.`,
      {
        heading: 'Delete role?',
        okLabel: 'Delete',
        cancelLabel: 'Cancel',
        danger: true,
      }
    );
    if (!confirmed) return;
    const res = await fetch(
      `${this._apiBase()}/${this._selectedRoleId}`,
      { method: 'DELETE', credentials: 'include' }
    );
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      await LubooDialog.alert(err.detail || 'Could not delete role.', {
        heading: 'Delete failed',
      });
      return;
    }
    this._selectedRoleId = null;
    await this.refresh();
  }

  async _openNewRoleDialog() {
    const result = await this._openRoleDialog({
      heading: 'New Role',
      defaultName: '',
      defaultDescription: '',
      okLabel: 'Create',
    });
    if (!result) return;
    const res = await fetch(this._apiBase(), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      credentials: 'include',
      body: JSON.stringify(result),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      await LubooDialog.alert(err.detail || 'Could not create role.', {
        heading: 'Create failed',
      });
      return;
    }
    const created = await res.json();
    this._selectedRoleId = created.id;
    await this.refresh();
  }

  async _openCloneRoleDialog(sourceRoleId) {
    const source = this._roles.find((r) => r.id === sourceRoleId);
    if (!source) return;
    const result = await this._openRoleDialog({
      heading: `Clone "${source.name}"`,
      defaultName: `${source.name} Copy`,
      defaultDescription: `Cloned from ${source.name}`,
      okLabel: 'Clone',
    });
    if (!result) return;
    const res = await fetch(
      `${this._apiBase()}/${sourceRoleId}/clone`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify(result),
      }
    );
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      await LubooDialog.alert(err.detail || 'Could not clone role.', {
        heading: 'Clone failed',
      });
      return;
    }
    const created = await res.json();
    this._selectedRoleId = created.id;
    await this.refresh();
  }

  _openRoleDialog({ heading, defaultName, defaultDescription, okLabel }) {
    return new Promise((resolve) => {
      const dlg = document.createElement('luboo-dialog');
      dlg.setAttribute('heading', heading);

      const form = document.createElement('div');
      form.className = 'custom-roles-dialog-form';

      const nameLabel = document.createElement('label');
      const nameSpan = document.createElement('span');
      nameSpan.textContent = 'Name';
      nameLabel.appendChild(nameSpan);
      const nameInput = document.createElement('input');
      nameInput.type = 'text';
      nameInput.className = 'dialog-input';
      nameInput.maxLength = 64;
      nameInput.required = true;
      nameInput.value = defaultName;
      nameLabel.appendChild(nameInput);
      form.appendChild(nameLabel);

      const descLabel = document.createElement('label');
      const descSpan = document.createElement('span');
      descSpan.textContent = 'Description';
      descLabel.appendChild(descSpan);
      const descTextarea = document.createElement('textarea');
      descTextarea.className = 'dialog-input';
      descTextarea.rows = 2;
      descTextarea.maxLength = 2000;
      descTextarea.value = defaultDescription;
      descLabel.appendChild(descTextarea);
      form.appendChild(descLabel);

      dlg.appendChild(form);

      const cancelBtn = document.createElement('button');
      cancelBtn.setAttribute('slot', 'actions');
      cancelBtn.setAttribute('value', 'cancel');
      cancelBtn.textContent = 'Cancel';
      dlg.appendChild(cancelBtn);

      const confirmBtn = document.createElement('button');
      confirmBtn.setAttribute('slot', 'actions');
      confirmBtn.setAttribute('value', 'confirm');
      confirmBtn.setAttribute('type', 'submit');
      confirmBtn.textContent = okLabel || 'Create';
      dlg.appendChild(confirmBtn);

      document.body.appendChild(dlg);

      (async () => {
        try {
          const result = await dlg.open();
          if (result !== 'confirm') {
            resolve(null);
            return;
          }
          const name = (nameInput.value || '').trim();
          if (!name) {
            resolve(null);
            return;
          }
          resolve({ name, description: (descTextarea.value || '').trim() });
        } finally {
          if (typeof dlg._destroy === 'function') dlg._destroy();
        }
      })();
    });
  }

  _escape(s) {
    return String(s || '')
      .replaceAll('&', '&amp;')
      .replaceAll('<', '&lt;')
      .replaceAll('>', '&gt;')
      .replaceAll('"', '&quot;');
  }

  _renderLoading() {
    this.innerHTML = `
      <div class="custom-roles-editor-loading">
        <p>Loading roles…</p>
      </div>
    `;
  }

  _renderError(msg) {
    this.innerHTML = `
      <div class="custom-roles-editor-error">
        <p>Couldn't load roles: ${this._escape(msg)}</p>
      </div>
    `;
  }

  _render() {
    this.innerHTML = `
      <div class="custom-roles-editor">
        ${this._renderRoleList()}
        ${this._renderEditorPane()}
      </div>
    `;
    this._applyPermFilter('available');
    this._applyPermFilter('granted');
  }

  _renderEditor() {
    const pane = this.querySelector('.custom-roles-editor-pane');
    if (pane) pane.outerHTML = this._renderEditorPane();
    this._refreshRoleListSelection();
    this._applyPermFilter('available');
    this._applyPermFilter('granted');
  }

  _refreshRoleListSelection() {
    this.querySelectorAll('[data-custom-roles-action="select-role"]').forEach(
      (el) => {
        const isSelected =
          el.getAttribute('data-role-id') === this._selectedRoleId;
        el.classList.toggle('is-selected', isSelected);
      }
    );
  }

  _refreshButtons() {
    const save = this.querySelector('[data-custom-roles-action="save"]');
    const cancel = this.querySelector('[data-custom-roles-action="cancel"]');
    if (save) save.toggleAttribute('disabled', !this._dirty);
    if (cancel) cancel.toggleAttribute('disabled', !this._dirty);
  }

  _renderRoleList() {
    const items = this._roles.map((role) => this._roleListItemHtml(role)).join('');
    return `
      <aside class="custom-roles-list">
        <div class="custom-roles-list-header">
          <h4>Roles</h4>
          <button class="btn btn-primary btn-sm"
                  data-custom-roles-action="new-role">
            <svg-icon icon="luboo:plus"></svg-icon> New Role
          </button>
        </div>
        <ul>${items}</ul>
      </aside>
    `;
  }

  _renderEditorPane() {
    if (!this._selectedDetail) {
      return `
        <section class="custom-roles-editor-pane">
          <p class="empty-state">Select a role to view or edit its permissions.</p>
        </section>
      `;
    }
    const isSystem = this._selectedDetail.is_system;
    const granted = this._draftPermissions;
    const draggable = isSystem ? '' : 'draggable="true"';
    const groupHtml = Object.entries(this._catalog)
      .map(([groupName, perms]) => {
        const available = perms.filter((p) => !granted.has(p.name));
        if (available.length === 0) {
          return `<div class="perm-group is-empty"><h5>${this._escape(groupName)}</h5></div>`;
        }
        return `
          <div class="perm-group">
            <h5>${this._escape(groupName)}</h5>
            <ul class="perm-list">
              ${available
                .map(
                  (p) => `
                <li class="perm-card"
                    data-perm-card data-permission="${this._escape(p.name)}"
                    data-perm-source="available"
                    ${draggable}
                    ${p.description ? '' : `title="${this._escape(p.name)}"`}>
                  <div class="perm-card-text">
                    <code>${this._escape(p.name)}</code>
                    ${p.description ? `<span class="perm-description">${this._escape(p.description)}</span>` : ''}
                  </div>
                  ${
                    isSystem
                      ? ''
                      : `<button class="btn btn-link btn-xs"
                              data-custom-roles-action="grant-permission"
                              data-permission="${this._escape(p.name)}"
                              aria-label="Grant ${this._escape(p.name)}">+</button>`
                  }
                </li>
              `
                )
                .join('')}
            </ul>
          </div>
        `;
      })
      .join('');

    const grantedList = Array.from(granted).sort();
    const descByName = {};
    for (const items of Object.values(this._catalog)) {
      for (const p of items) descByName[p.name] = p.description || '';
    }
    const grantedHtml = grantedList
      .map(
        (name) => `
          <li class="perm-card"
              data-perm-card data-permission="${this._escape(name)}"
              data-perm-source="granted"
              ${draggable}
              ${descByName[name] ? '' : `title="${this._escape(name)}"`}>
            <div class="perm-card-text">
              <code>${this._escape(name)}</code>
              ${descByName[name] ? `<span class="perm-description">${this._escape(descByName[name])}</span>` : ''}
            </div>
            ${
              isSystem
                ? ''
                : `<button class="btn btn-link btn-xs"
                        data-custom-roles-action="revoke-permission"
                        data-permission="${this._escape(name)}"
                        aria-label="Revoke ${this._escape(name)}">×</button>`
            }
          </li>
        `
      )
      .join('');
    return `
      <section class="custom-roles-editor-pane${isSystem ? ' is-system-role' : ''}">
        <div class="role-meta">
          <label>
            <span>Name</span>
            <input type="text" data-custom-roles-field="name"
                   value="${this._escape(this._draftName)}"
                   maxlength="64" ${isSystem ? 'readonly' : ''}>
          </label>
          <label>
            <span>Description</span>
            <textarea data-custom-roles-field="description"
                      maxlength="2000" rows="2"
                      ${isSystem ? 'readonly' : ''}>${this._escape(this._draftDescription)}</textarea>
          </label>
          ${
            isSystem
              ? `<div class="system-role-note">
                  <p>System roles are read-only. Use <strong>Clone</strong> to create an editable copy.</p>
                  <button class="btn btn-primary btn-sm"
                          data-custom-roles-action="clone-role"
                          data-role-id="${this._escape(this._selectedDetail.id)}">
                    <svg-icon icon="luboo:copy"></svg-icon> Clone this role
                  </button>
                </div>`
              : `<div class="role-meta-actions">
                  <button class="btn btn-secondary btn-sm"
                          data-custom-roles-action="clone-role"
                          data-role-id="${this._escape(this._selectedDetail.id)}">
                    <svg-icon icon="luboo:copy"></svg-icon> Clone
                  </button>
                  <a href="#"
                     class="custom-roles-delete-link"
                     data-custom-roles-action="delete-role">
                    Delete role
                  </a>
                </div>`
          }
        </div>
        <div class="permission-columns">
          <div class="available-column" data-perm-drop-target="available">
            <h4>Available</h4>
            <input type="search"
                   class="filter-search custom-roles-perm-filter"
                   data-perm-filter="available"
                   placeholder="Filter permissions…"
                   value="${this._escape(this._filterAvailable || '')}">
            ${groupHtml}
          </div>
          <div class="granted-column" data-perm-drop-target="granted">
            <h4>Granted (${granted.size})</h4>
            <input type="search"
                   class="filter-search custom-roles-perm-filter"
                   data-perm-filter="granted"
                   placeholder="Filter granted…"
                   value="${this._escape(this._filterGranted || '')}">
            <ul class="perm-list">${
              grantedHtml ||
              '<li class="empty-state">No permissions granted.</li>'
            }</ul>
          </div>
        </div>
        ${
          isSystem
            ? ''
            : `<div class="editor-actions">
                <button class="btn btn-secondary"
                        data-custom-roles-action="cancel"
                        ${this._dirty ? '' : 'disabled'}>
                  Cancel
                </button>
                <button class="btn btn-primary"
                        data-custom-roles-action="save"
                        ${this._dirty ? '' : 'disabled'}>
                  Save
                </button>
              </div>`
        }
      </section>
    `;
  }
}

export { CustomRolesEditor };
